import { useState } from 'react';
import { useCatIds, cat_id2lbl } from '../../lib/cat';

const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Brazil',
  'Mexico',
  'Japan',
  'South Korea',
  'India',
  'Singapore',
  'UAE',
  'Israel',
];

const AGE_RANGES = [
  { min: 13, max: 17, label: '13-17' },
  { min: 18, max: 24, label: '18-24' },
  { min: 25, max: 34, label: '25-34' },
  { min: 35, max: 44, label: '35-44' },
  { min: 45, max: 54, label: '45-54' },
  { min: 55, max: 64, label: '55-64' },
  { min: 65, max: 100, label: '65+' },
];

const GENDERS = [
  { value: 'any', label: 'Any Gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

interface Props {
  data: any;
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function CampaignTargeting({ data, onChange, onNext, onBack }: Props) {
  const [searchCountry, setSearchCountry] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const { ids: cat_ids } = useCatIds();

  const handleCountryToggle = (country: string) => {
    const countries = data.targeting.countries || [];
    const newCountries = countries.includes(country)
      ? countries.filter((c: string) => c !== country)
      : [...countries, country];
    
    onChange('targeting', {
      ...data.targeting,
      countries: newCountries,
    });
  };

  const handleAgeRangeToggle = (range: { min: number; max: number; label: string }) => {
    const ageRanges = data.targeting.age_ranges || [];
    const rangeArray = [range.min, range.max];
    const exists = ageRanges.some((r: number[]) => r[0] === range.min && r[1] === range.max);
    
    const newRanges = exists
      ? ageRanges.filter((r: number[]) => !(r[0] === range.min && r[1] === range.max))
      : [...ageRanges, rangeArray];
    
    onChange('targeting', {
      ...data.targeting,
      age_ranges: newRanges,
    });
  };

  const handleGenderToggle = (gender: string) => {
    const genders = data.targeting.genders || [];
    const newGenders = genders.includes(gender)
      ? genders.filter((g: string) => g !== gender)
      : [...genders, gender];
    
    onChange('targeting', {
      ...data.targeting,
      genders: newGenders,
    });
  };

  const handleCategoryToggle = (category: string) => {
    const categories = data.targeting.content_categories || [];
    const newCategories = categories.includes(category)
      ? categories.filter((c: string) => c !== category)
      : [...categories, category];
    
    onChange('targeting', {
      ...data.targeting,
      content_categories: newCategories,
    });
  };

  const filteredCountries = COUNTRIES.filter(c =>
    c.toLowerCase().includes(searchCountry.toLowerCase())
  );

  const filteredCategories = cat_ids.filter(id =>
    cat_id2lbl(id).toLowerCase().includes(searchCategory.toLowerCase())
  );

  const isAgeRangeSelected = (range: { min: number; max: number }) => {
    const ageRanges = data.targeting.age_ranges || [];
    return ageRanges.some((r: number[]) => r[0] === range.min && r[1] === range.max);
  };

  const handleNext = () => {
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Target Audience
        </h3>
        <p className="text-gray-600">
          Define who should see your campaign
        </p>
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Countries <span className="text-gray-400 text-xs">(Optional - Leave empty for all countries)</span>
        </label>
        
        <input
          type="text"
          placeholder="Search countries..."
          value={searchCountry}
          onChange={(e) => setSearchCountry(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg bg-gray-50">
          {filteredCountries.map((country) => (
            <label
              key={country}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                data.targeting.countries?.includes(country)
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              <input
                type="checkbox"
                checked={data.targeting.countries?.includes(country) || false}
                onChange={() => handleCountryToggle(country)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium truncate">{country}</span>
            </label>
          ))}
        </div>
        
        {data.targeting.countries?.length > 0 && (
          <p className="text-sm text-blue-600 mt-2">
            {data.targeting.countries.length} {data.targeting.countries.length === 1 ? 'country' : 'countries'} selected
          </p>
        )}
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Age Range <span className="text-gray-400 text-xs">(Optional - Leave empty for all ages)</span>
        </label>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {AGE_RANGES.map((range) => (
            <button
              key={range.label}
              type="button"
              onClick={() => handleAgeRangeToggle(range)}
              className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                isAgeRangeSelected(range)
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        
        {data.targeting.age_ranges?.length > 0 && (
          <p className="text-sm text-blue-600 mt-2">
            {data.targeting.age_ranges.length} age {data.targeting.age_ranges.length === 1 ? 'range' : 'ranges'} selected
          </p>
        )}
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Gender <span className="text-gray-400 text-xs">(Optional - Leave empty for all genders)</span>
        </label>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {GENDERS.map((gender) => (
            <button
              key={gender.value}
              type="button"
              onClick={() => handleGenderToggle(gender.value)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                data.targeting.genders?.includes(gender.value)
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {gender.label}
            </button>
          ))}
        </div>
      </div>

      {}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Content Categories <span className="text-gray-400 text-xs">(Optional - Leave empty for all content)</span>
        </label>
        
        <input
          type="text"
          placeholder="Search categories..."
          value={searchCategory}
          onChange={(e) => setSearchCategory(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg bg-gray-50">
          {filteredCategories.map((id) => (
            <label
              key={id}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                data.targeting.content_categories?.includes(id)
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                  : 'bg-white hover:bg-gray-100'
              }`}
            >
              <input
                type="checkbox"
                checked={data.targeting.content_categories?.includes(id) || false}
                onChange={() => handleCategoryToggle(id)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium truncate">{cat_id2lbl(id)}</span>
            </label>
          ))}
        </div>
        
        {data.targeting.content_categories?.length > 0 && (
          <p className="text-sm text-blue-600 mt-2">
            {data.targeting.content_categories.length} {data.targeting.content_categories.length === 1 ? 'category' : 'categories'} selected
          </p>
        )}
      </div>

      {}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">Targeting Tips</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• All fields are optional - leave empty to target all audiences</li>
              <li>• More specific targeting = higher relevance but smaller reach</li>
              <li>• Content categories help match your ad with relevant creators</li>
            </ul>
          </div>
        </div>
      </div>

      {}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
        >
          Continue to Budget →
        </button>
      </div>
    </div>
  );
}